/* eslint-disable new-cap */
/**
 * REST API を使用した配信履歴取得 hook
 * OpenAPI仕様から生成された型を使用（SSOT）
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { components } from "../api/schema";

// =====================================
// 生成された型を再エクスポート
// =====================================

export type DeliveryHistory = components["schemas"]["DeliveryHistory"];
export type DeliveryHistoriesResponse =
  components["schemas"]["DeliveryHistoriesResponse"];
export type DeliveryHistorySearchInput =
  components["schemas"]["DeliveryHistorySearchInput"];

// 値として使用可能なオブジェクト（既存コードとの互換性）
export const DeliveryEmailActionType = {
  Send: "Send",
  Draft: "Draft",
  Test: "Test",
  Scheduled: "Scheduled",
  Immediately: "Immediately",
} as const;

export type DeliveryEmailActionType =
  (typeof DeliveryEmailActionType)[keyof typeof DeliveryEmailActionType];

// DeliveryHistories型（既存コードとの互換性）
export interface DeliveryHistories {
  totalCount: number;
  nodes: DeliveryHistory[];
}

// =====================================
// REST API Hook Types
// =====================================

export interface UseDeliveryHistoriesRestApiOptions {
  variables?: {
    filter?: DeliveryHistorySearchInput | null;
    offset?: number;
    limit?: number;
  };
  onCompleted?: () => void;
}

export interface UseDeliveryHistoriesRestApiResult {
  data: { deliveryHistories: DeliveryHistories } | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: (variables?: {
    filter?: DeliveryHistorySearchInput | null;
    offset?: number;
    limit?: number;
  }) => Promise<{ deliveryHistories: DeliveryHistories }>;
}

/**
 * 配信履歴一覧を REST API から取得する hook
 * @param {UseDeliveryHistoriesRestApiOptions} options - フック設定オプション
 * @return {UseDeliveryHistoriesRestApiResult} 配信履歴データ、エラー、ローディング状態、再取得関数を含むオブジェクト
 */
export function useDeliveryHistoriesRestApi(
  options: UseDeliveryHistoriesRestApiOptions = {},
): UseDeliveryHistoriesRestApiResult {
  const [data, setData] = useState<
    { deliveryHistories: DeliveryHistories } | undefined
  >(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchDeliveryHistories = useCallback(
    async (variables?: {
      filter?: DeliveryHistorySearchInput | null;
      offset?: number;
      limit?: number;
    }): Promise<{ deliveryHistories: DeliveryHistories }> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {};

        const vars = variables || options.variables;
        if (vars?.offset !== undefined) {
          params.offset = String(vars.offset);
        }
        if (vars?.limit !== undefined) {
          params.limit = String(vars.limit);
        }
        if (vars?.filter) {
          params.filter = JSON.stringify(vars.filter);
        }

        const { data: responseData, error: apiError } = await api.GET(
          "/api/delivery/histories",
          {
            params: {
              query: Object.keys(params).length > 0 ? params : undefined,
            },
          },
        );

        if (apiError) {
          throw new Error(
            (apiError as { message?: string }).message || "API error",
          );
        }

        if (!responseData) {
          throw new Error("No data received");
        }

        const result = {
          deliveryHistories: {
            totalCount: responseData.totalCount,
            nodes: responseData.nodes,
          },
        };

        setData(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
        options.onCompleted?.();
      }
    },
    [options.onCompleted, options.variables],
  );

  const initialFetchDone = useRef(false);
  const variablesRef = useRef(options.variables);

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchDeliveryHistories(variablesRef.current).catch(() => {
        // エラーは state で管理
      });
    }
  }, [fetchDeliveryHistories]);

  const refetch = useCallback(
    async (variables?: {
      filter?: DeliveryHistorySearchInput | null;
      offset?: number;
      limit?: number;
    }): Promise<{ deliveryHistories: DeliveryHistories }> => {
      return fetchDeliveryHistories(variables || options.variables);
    },
    [fetchDeliveryHistories, options.variables],
  );

  return {
    data,
    error,
    loading,
    refetch,
  };
}

export default useDeliveryHistoriesRestApi;
